import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sparkle, ShieldCheck, WarningOctagon, Gauge, Prohibit, FileXls, FilePdf } from "@phosphor-icons/react";
import { Switch } from "../components/ui/switch";
import { CONTROL_TYPE_META, RISK_BAND_META, ControlTypeBadge, RiskBandBadge } from "../lib/controlTypes";
import GlossaryText from "../components/GlossaryText";

// Full requirement + subpart language with hover-to-define defined terms.
const ReqLang = ({ text, parts, testid }) => (
  <div className="text-xs text-muted-foreground max-w-2xl space-y-1" data-testid={testid}>
    {text && <p className="whitespace-pre-line leading-relaxed"><GlossaryText text={text} /></p>}
    {(parts || []).filter((p) => (p.text || "").trim()).map((p) => (
      <p key={p.label} className="whitespace-pre-line leading-relaxed pl-3 border-l border-border">
        <span className="font-mono font-semibold text-foreground/70 mr-1">{p.label}</span><GlossaryText text={p.text} />
      </p>
    ))}
  </div>
);

const BANDS = ["severe", "high", "medium", "low", "none"];
const SCALE = [1, 2, 3, 4, 5];
const BOOSTERS = [
  ["prior_violation", "Prior non-compliance"],
  ["ero_focus", "ERO Focus Area"],
  ["top10", "Top-10 violation list"],
  ["assessment_overdue", "Assessment overdue"],
];

export default function RiskMatrix() {
  const { canEdit, isRole, aiEnabled } = useAuth();
  const isAdmin = isRole("admin");
  const [rows, setRows] = useState([]);
  const [naRows, setNaRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [bandFilter, setBandFilter] = useState("all");
  const [gapsOnly, setGapsOnly] = useState(false);
  const [view, setView] = useState("risk");
  const [sel, setSel] = useState(null);
  const [evidenceAware, setEvidenceAware] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/risk-matrix").then((r) => { setRows(r.data.requirements); setNaRows(r.data.not_applicable || []); setSummary(r.data.summary); }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/risk-ai-settings").then((r) => setEvidenceAware(!!r.data.evidence_aware)).catch(() => {}); }, []);

  const toggleEvidence = async (v) => {
    setEvidenceAware(v);
    try { await api.put("/risk-ai-settings", { evidence_aware: v }); toast.success(`Evidence-aware AI ${v ? "on" : "off"}`); }
    catch (e) { setEvidenceAware(!v); toast.error("Couldn't update setting"); }
  };

  const shown = useMemo(() => rows.filter((r) => (bandFilter === "all" || r.risk.residual_band === bandFilter) && (!gapsOnly || !r.risk.coverage.meets)), [rows, bandFilter, gapsOnly]);

  const exportRegister = async (fmt) => {
    try {
      const res = await api.get(`/risk-matrix/export?format=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `risk_register_${new Date().toISOString().slice(0, 10)}.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Risk register exported (${fmt.toUpperCase()})`);
    } catch (e) { toast.error("Export failed — please try again."); }
  };

  return (
    <div>
      <PageHeader title="Requirement Risk Matrix" subtitle="Applicable requirements ranked by residual risk — Likelihood × Impact, reduced by your internal controls"
        action={<div className="flex items-center gap-3">
          {isAdmin && (
            <div className="flex items-center gap-2 border border-border px-3 py-1.5" title="When on, the AI also reviews mapped evidence and flags controls/SOPs marked effective but with no evidence on file.">
              <Switch data-testid="evidence-aware-toggle" checked={evidenceAware} onCheckedChange={toggleEvidence} />
              <span className="text-xs font-semibold">Evidence-aware AI</span>
            </div>
          )}
          <Button data-testid="risk-export-xlsx" onClick={() => exportRegister("xlsx")} variant="outline" className="rounded-none" disabled={rows.length === 0 && naRows.length === 0}><FileXls size={16} className="mr-1" />Excel</Button>
          <Button data-testid="risk-export-pdf" onClick={() => exportRegister("pdf")} variant="outline" className="rounded-none" disabled={rows.length === 0 && naRows.length === 0}><FilePdf size={16} className="mr-1" />PDF</Button>
        </div>} />
      <div className="p-8">
        {/* Heat-map summary */}
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-6">
          <button data-testid="risk-summary-all" onClick={() => { setBandFilter("all"); setGapsOnly(false); setView("risk"); }} className={`border p-4 text-left rounded-none ${view === "risk" && bandFilter === "all" && !gapsOnly ? "border-primary ring-1 ring-primary" : "border-border"} bg-card`}>
            <div className="flex items-center gap-1 text-muted-foreground"><Gauge size={14} /><span className="overline">Applicable</span></div>
            <div className="font-display font-black text-2xl mt-1">{rows.length}</div>
          </button>
          {BANDS.filter((b) => b !== "none").map((b) => (
            <button key={b} data-testid={`risk-summary-${b}`} onClick={() => { setBandFilter(b); setGapsOnly(false); setView("risk"); }} className={`border p-4 text-left rounded-none ${view === "risk" && bandFilter === b ? "ring-1 ring-primary" : ""} ${RISK_BAND_META[b].tone}`}>
              <span className="overline">{RISK_BAND_META[b].label}</span>
              <div className="font-display font-black text-2xl mt-1">{summary[b] || 0}</div>
            </button>
          ))}
          <button data-testid="risk-summary-gaps" onClick={() => { setGapsOnly(true); setBandFilter("all"); setView("risk"); }} className={`border p-4 text-left rounded-none ${view === "risk" && gapsOnly ? "ring-1 ring-primary" : ""} bg-red-50 text-red-700 border-red-300`}>
            <div className="flex items-center gap-1"><WarningOctagon size={14} /><span className="overline">Coverage Gaps</span></div>
            <div className="font-display font-black text-2xl mt-1">{summary.gaps || 0}</div>
          </button>
          <button data-testid="risk-summary-na" onClick={() => setView("na")} className={`border p-4 text-left rounded-none ${view === "na" ? "border-primary ring-1 ring-primary" : "border-border"} bg-secondary/40`}>
            <div className="flex items-center gap-1 text-muted-foreground"><Prohibit size={14} /><span className="overline">Not Applicable</span></div>
            <div className="font-display font-black text-2xl mt-1">{naRows.length}</div>
          </button>
        </div>

        {loading ? <div className="font-mono text-sm text-muted-foreground">Loading risk matrix…</div> : view === "na" ? (
          naRows.length === 0 ? <Empty title="No out-of-scope requirements" hint="Every requirement applies under the current Entity Profile." /> : (
            <>
              <p className="text-sm text-muted-foreground mb-3">Requirements <strong>not applicable</strong> to this registration, with the documented reason — your audit-ready scoping rationale.</p>
              <div className="border border-border bg-card overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-border bg-secondary/40 text-left overline text-muted-foreground">
                    <th className="px-3 py-3">Requirement</th>
                    <th className="px-3 py-3">Status</th>
                    <th className="px-3 py-3">Why it is Not Applicable</th>
                  </tr></thead>
                  <tbody>
                    {naRows.map((r) => (
                      <tr key={r.id} data-testid={`na-row-${r.id}`} className="border-b border-border hover:bg-secondary/20">
                        <td className="px-3 py-3 align-top">
                          <div className="font-mono font-bold">{r.standard_id} {r.req_number}</div>
                          <ReqLang text={r.text} parts={r.parts} testid={`na-lang-${r.id}`} />
                        </td>
                        <td className="px-3 py-3 align-top capitalize text-xs">{(r.status || "").replace(/_/g, " ")}</td>
                        <td className="px-3 py-3 align-top text-xs text-muted-foreground max-w-xl" data-testid={`na-reason-${r.id}`}>{r.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )
        ) : shown.length === 0 ? (
          <Empty title="No requirements match" hint="Adjust the filter, or set your Entity Profile so applicable requirements appear." />
        ) : (
          <div className="border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border bg-secondary/40 text-left overline text-muted-foreground">
                <th className="px-3 py-3">Requirement</th>
                <th className="px-3 py-3">L × I</th>
                <th className="px-3 py-3">Inherent</th>
                <th className="px-3 py-3">Controls</th>
                <th className="px-3 py-3">Residual</th>
                <th className="px-3 py-3">Coverage</th>
                <th className="px-3 py-3"></th>
              </tr></thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id} data-testid={`risk-row-${r.id}`} className="border-b border-border hover:bg-secondary/20">
                    <td className="px-3 py-3 align-top">
                      <div className="font-mono font-bold">{r.standard_id} {r.req_number}</div>
                      <ReqLang text={r.text} parts={r.parts} testid={`risk-lang-${r.id}`} />
                    </td>
                    <td className="px-3 py-3 align-top font-mono">{r.risk.likelihood} × {r.risk.impact}</td>
                    <td className="px-3 py-3 align-top"><RiskBandBadge band={r.risk.inherent_band} score={r.risk.inherent} /></td>
                    <td className="px-3 py-3 align-top">
                      {r.controls.length === 0 ? <span className="text-xs text-muted-foreground">None</span> : (
                        <div className="flex flex-wrap gap-1">{r.controls.map((c) => <ControlTypeBadge key={c.id} type={c.control_type} />)}</div>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top"><RiskBandBadge band={r.risk.residual_band} score={r.risk.residual} testid={`risk-residual-${r.id}`} /></td>
                    <td className="px-3 py-3 align-top">
                      {r.risk.coverage.meets ? <span className="text-green-700 text-xs font-semibold flex items-center gap-1"><ShieldCheck size={14} />Covered</span>
                        : <span data-testid={`risk-gap-${r.id}`} className="text-red-600 text-xs font-semibold">Needs {r.risk.coverage.gap}</span>}
                    </td>
                    <td className="px-3 py-3 align-top">
                      <Button data-testid={`risk-assess-${r.id}`} variant="outline" size="sm" className="rounded-none" onClick={() => setSel(r)}>Assess</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {sel && <AssessDialog row={sel} canEdit={canEdit} aiEnabled={aiEnabled} onClose={() => setSel(null)} onSaved={() => { load(); setSel(null); }} />}
    </div>
  );
}

function AssessDialog({ row, canEdit, aiEnabled, onClose, onSaved }) {
  const r = row.risk;
  const [form, setForm] = useState({
    likelihood: r.likelihood, impact: r.impact,
    likelihood_overridden: r.likelihood_overridden, impact_overridden: r.impact_overridden,
    residual_likelihood: r.residual_likelihood || "",
    vrf: r.vrf || "", notes: r.notes || "", ...r.boosters,
  });
  const [aiBusy, setAiBusy] = useState(false);
  const [recBusy, setRecBusy] = useState(false);
  const [rec, setRec] = useState(null);

  const aiSeed = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/requirements/${row.id}/risk/ai-seed`);
      const note = [data.rationale, data.control_adequacy && `Existing coverage: ${data.control_adequacy}`].filter(Boolean).join("\n\n");
      setForm((f) => ({ ...f, likelihood: data.likelihood, impact: data.impact, residual_likelihood: data.residual_likelihood, likelihood_overridden: true, impact_overridden: true, vrf: data.vrf || f.vrf, notes: note || f.notes }));
      const rv = data.reviewed || {};
      toast.success(`AI reviewed ${rv.controls || 0} control(s) + ${rv.documents || 0} SOP/doc(s) — residual likelihood ${data.residual_likelihood}/5`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  const save = async () => {
    try {
      await api.put(`/requirements/${row.id}/risk`, {
        likelihood: form.likelihood, impact: form.impact,
        likelihood_overridden: form.likelihood_overridden, impact_overridden: form.impact_overridden,
        residual_likelihood: form.residual_likelihood ? parseInt(form.residual_likelihood) : null,
        vrf: form.vrf || null, notes: form.notes,
        prior_violation: !!form.prior_violation, ero_focus: !!form.ero_focus, top10: !!form.top10, assessment_overdue: !!form.assessment_overdue,
      });
      toast.success("Risk assessment saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const recommend = async () => {
    setRecBusy(true);
    try {
      const { data } = await api.post(`/requirements/${row.id}/recommend-controls`);
      setRec(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRecBusy(false); }
  };

  const createControl = async (c) => {
    try {
      await api.post("/controls", {
        name: c.name, objective: c.objective || "", method: c.method || "",
        frequency: c.frequency || "As needed", control_type: c.control_type || "preventative",
        operating_status: "designed", effectiveness: "not_tested", linked_requirements: [row.id],
      });
      toast.success(`Control created: ${c.name}`);
      setRec((p) => ({ ...p, recommendations: p.recommendations.filter((x) => x !== c) }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="risk-assess-dialog">
        <DialogHeader><DialogTitle>{row.standard_id} {row.req_number} — Risk Assessment</DialogTitle></DialogHeader>
        <div className="-mt-2"><ReqLang text={row.text} parts={row.parts} testid="risk-dialog-lang" /></div>

        <div className="flex items-center gap-2 my-2 flex-wrap">
          <RiskBandBadge band={r.inherent_band} score={r.inherent} /> <span className="text-xs text-muted-foreground">inherent (before controls)</span>
          <span className="text-muted-foreground">→</span>
          <RiskBandBadge band={r.residual_band} score={r.residual} testid="risk-residual-headline" />
          <span className="text-xs text-muted-foreground">residual {r.residual_basis === "assessed" ? "(assessed with your controls & SOPs)" : `(after ${r.control_count} control${r.control_count === 1 ? "" : "s"})`}</span>
        </div>

        {canEdit && (
          <div className="border border-border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-display font-bold text-sm">Likelihood & Impact</h4>
              <Button data-testid="risk-ai-seed-btn" variant="outline" size="sm" className="rounded-none" onClick={aiSeed} disabled={aiBusy || !aiEnabled} title={!aiEnabled ? "AI features are turned off for this entity" : undefined}><Sparkle size={14} className="mr-1" />{aiBusy ? "Assessing…" : "AI assess"}</Button>
            </div>
            <p className="text-[11px] text-muted-foreground">Auto-seeded from cadence ({r.frequency_label}) & VRF (AI estimate {r.auto_likelihood} × {r.auto_impact}). <strong>AI assess</strong> reviews your existing internal controls & SOPs before scoring. Override below.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Likelihood (1–5)</Label>
                <Select value={String(form.likelihood)} onValueChange={(v) => setForm({ ...form, likelihood: parseInt(v), likelihood_overridden: true })}>
                  <SelectTrigger data-testid="risk-likelihood-select" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{SCALE.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Impact (1–5)</Label>
                <Select value={String(form.impact)} onValueChange={(v) => setForm({ ...form, impact: parseInt(v), impact_overridden: true })}>
                  <SelectTrigger data-testid="risk-impact-select" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{SCALE.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {r.likelihood_breakdown && (
              <div className="text-[11px] bg-secondary/40 border border-border px-2 py-1.5" data-testid="likelihood-breakdown">
                <span className="font-semibold">Auto likelihood {r.likelihood_breakdown.auto}</span> = base {r.likelihood_breakdown.base} ({r.likelihood_breakdown.base_reason})
                {r.likelihood_breakdown.booster_points > 0 && <> + {r.likelihood_breakdown.booster_points} booster ({r.likelihood_breakdown.booster_labels.join(", ")})</>}
                {r.likelihood_breakdown.overridden && <span className="text-amber-700"> · currently overridden to {r.likelihood}</span>}
              </div>
            )}
            <div><Label className="text-xs">NERC VRF (anchors Impact)</Label>
              <Select value={form.vrf || "unset"} onValueChange={(v) => setForm({ ...form, vrf: v === "unset" ? "" : v })}>
                <SelectTrigger data-testid="risk-vrf-select" className="rounded-none mt-1"><SelectValue placeholder="Not set" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">Not set</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Lower">Lower</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Residual likelihood — after existing controls & SOPs (1–5)</Label>
              <Select value={form.residual_likelihood ? String(form.residual_likelihood) : "auto"} onValueChange={(v) => setForm({ ...form, residual_likelihood: v === "auto" ? "" : parseInt(v) })}>
                <SelectTrigger data-testid="risk-residual-likelihood-select" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (model from mapped controls)</SelectItem>
                  {SCALE.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">Lower this when strong controls/SOPs are in place — it directly drives the residual risk level. Leave on Auto to model it from your mapped controls.</p>
            </div>
            <div>
              <Label className="text-xs">Likelihood boosters</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {BOOSTERS.map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox data-testid={`risk-booster-${k}`} checked={!!form[k]} onCheckedChange={(v) => setForm({ ...form, [k]: !!v })} className="rounded-none" />{l}
                  </label>
                ))}
              </div>
            </div>
            <div><Label className="text-xs">Rationale / notes</Label><Textarea data-testid="risk-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none mt-1" rows={3} /></div>
            <Button data-testid="risk-save-btn" onClick={save} className="rounded-none w-full bg-primary hover:bg-primary/90">Save Assessment</Button>
          </div>
        )}

        {/* Control coverage */}
        <div className="border border-border p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-display font-bold text-sm">Internal Controls ({row.controls.length})</h4>
            {canEdit && <Button data-testid="recommend-controls-btn" variant="outline" size="sm" className="rounded-none" onClick={recommend} disabled={recBusy || !aiEnabled} title={!aiEnabled ? "AI features are turned off for this entity" : undefined}><Sparkle size={14} className="mr-1" />{recBusy ? "Thinking…" : "Recommend controls"}</Button>}
          </div>
          {!r.coverage.meets && <div className="text-xs text-red-600 font-semibold" data-testid="risk-coverage-gap">Coverage gap: needs {r.coverage.gap} control</div>}
          {row.controls.length > 0 && (
            <div className="space-y-1">
              {row.controls.map((c) => (
                <div key={c.id} className="flex items-center gap-2 text-sm border border-border px-2 py-1.5">
                  <ControlTypeBadge type={c.control_type} /><span>{c.name}</span>
                </div>
              ))}
            </div>
          )}
          {rec && (
            <div className="mt-2 border-t border-border pt-3 space-y-3" data-testid="recommendations-panel">
              {rec.failure_modes?.length > 0 && (
                <div>
                  <div className="overline text-muted-foreground">Potential failure modes</div>
                  <ul className="list-disc pl-5 text-sm text-muted-foreground mt-1">{rec.failure_modes.map((f, i) => <li key={`fm-${i}-${f.slice(0, 24)}`}>{f}</li>)}</ul>
                </div>
              )}
              <div className="overline text-muted-foreground">Recommended controls</div>
              {(rec.recommendations || []).length === 0 ? <div className="text-xs text-muted-foreground">No new recommendations.</div> : rec.recommendations.map((c, i) => (
                <div key={`${c.control_type}-${c.name || i}`} data-testid={`recommendation-${i}`} className="border border-border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2"><ControlTypeBadge type={c.control_type} /><span className="font-semibold text-sm">{c.name}</span></div>
                    {canEdit && <Button data-testid={`create-recommended-${i}`} size="sm" className="rounded-none bg-primary hover:bg-primary/90" onClick={() => createControl(c)}>Create</Button>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{c.objective}</p>
                  {c.method && <p className="text-xs mt-1"><span className="text-muted-foreground">How: </span>{c.method}</p>}
                  {c.rationale && <p className="text-xs mt-1 italic text-muted-foreground">{c.rationale}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
